"use client";

import React from "react";
import { Loader, CircleCheck as CheckCircle2 } from 'lucide-react';
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="size-16 bg-primary/10 text-primary rounded-lg flex items-center justify-center mb-6">
          <CheckCircle2 className="size-8" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground mb-2">
          Message Sent!
        </h3>
        <p className="text-muted-foreground mb-8 max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Name</FormLabel>
              <FormControl>
                <Input placeholder="Your Name" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground">Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="your.email@example.com" className="bg-background" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground">Message</FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you?" className="min-h-[150px] bg-background resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive text-sm">
          {submitError}
        </div>}

      <div className="flex justify-center pt-4">
        <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending...
            </> : "Submit"}
        </Button>
      </div>
    </Form>;
}
export default function ContactForm() {
  return <section id="contact-form" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
            Contact Us
          </h2>
          <p className="text-muted-foreground text-lg">
            Have a question or want to learn more? Fill out the form below and we'll be in touch.
          </p>
        </div>

        <div className="bg-card p-6 md:p-10 rounded-xl shadow-sm border border-border">
          <FormProvider formId="contact-us-form" sectionName="Contact Form" defaultValues={{
          name: "",
          email: "",
          message: ""
        }} validationRules={{
          name: {
            required: true,
            minLength: 2
          },
          email: {
            required: true,
            pattern: "email"
          },
          message: {
            required: true,
            minLength: 10
          }
        }} successMessage="Thank you for contacting us. We will get back to you soon.">
            <ContactFormContent />
          </FormProvider>
        </div>
      </div>
    </section>;
}